import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, debounceTime, distinctUntilChanged } from 'rxjs';
import { toast } from 'ngx-sonner';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { SharedModule } from 'primeng/api';
import { SettingsService } from '../../../../../../core/services/settings.service';
import { AppButton } from '../../../../../../shared/components/app-button/app-button';

/**
 * Points one AI-provider diagnosis at a diagnosis the clinic owns. The
 * clinic's diagnosis is the one carrying treatments and prices, so this link
 * is what lets an imported finding produce a usable treatment plan.
 */
@Component({
  selector: 'app-dtp-link-diagnosis-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    Dialog,
    SharedModule,
    InputText,
    IconField,
    InputIcon,
    AppButton,
  ],
  templateUrl: './dtp-link-diagnosis-modal.html',
})
export class DtpLinkDiagnosisModal implements OnInit {
  /** The provider entry being linked (global row). */
  @Input() providerDiagnostic!: any;
  @Input() branchId!: number;
  /** Currently linked master id, when the entry is already linked. */
  @Input() currentMasterId: number | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  private all: any[] = [];
  visible: any[] = [];
  selectedId: number | null = null;
  loading = false;
  saving = false;
  searchControl = new FormControl('');

  async ngOnInit() {
    this.selectedId = this.currentMasterId;
    await this.load();
    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => this.filter());
  }

  private async load() {
    this.loading = true;
    this.cdr.detectChanges();
    try {
      this.all = await firstValueFrom(this.settingsService.getBranchOwnDiagnostics(this.branchId));
      this.filter();
    } catch (err) {
      console.error('Error loading diagnoses for linking:', err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private filter() {
    const s = (this.searchControl.value ?? '').trim().toLowerCase();
    this.visible = s
      ? this.all.filter((d) =>
          String(d.name ?? '')
            .toLowerCase()
            .includes(s)
        )
      : this.all;
    this.cdr.detectChanges();
  }

  pick(d: any) {
    this.selectedId = this.selectedId === d.id ? null : d.id;
  }

  async save() {
    if (this.saving || !this.selectedId) return;
    this.saving = true;
    try {
      await firstValueFrom(
        this.settingsService.upsertDiagnosisLink(
          this.branchId,
          this.providerDiagnostic,
          this.selectedId
        )
      );
      toast.success(this.translate.instant('NOTIFY_SAVED_SUCCESS'));
      this.saved.emit();
    } catch (err) {
      console.error('Error saving the diagnosis link:', err);
      toast.error(this.translate.instant('error_saving_data'));
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }

  async unlink() {
    if (this.saving || !this.currentMasterId) return;
    this.saving = true;
    try {
      await firstValueFrom(
        this.settingsService.removeDiagnosisLink(this.branchId, this.providerDiagnostic)
      );
      toast.success(this.translate.instant('NOTIFY_SAVED_SUCCESS'));
      this.saved.emit();
    } catch (err) {
      console.error('Error removing the diagnosis link:', err);
      toast.error(this.translate.instant('error_saving_data'));
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }
}
